import { isPhoneNumber } from "util/helper/isPhoneNumber";

export const REGEX_PATTERNS = {
  EMAIL:
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i, // from http://emailregex.com/
  NAME: /^[a-zA-ZÀ-ÖØ-öø-ÿ'\s-]+$/,
};

type VALIDATION_TYPE = "IS_EMAIL" | "MIN_LENGTH" | "EQUAL_FIELDS" | "PHONE_NUMBER" | "IS_NAME";

export interface Validation {
  validationType: VALIDATION_TYPE;
  elementName: string;
  minLength?: number;
  comparingElementValue?: string;
  validationError?: string;
}

export function matchRegexp<V>(value: V, regexp: RegExp) {
  return !value || regexp.test(`${value}`);
}

export const validations = {

  isEmail: (value: string, validationError?: string) => (
    matchRegexp(value, REGEX_PATTERNS.EMAIL) ? "" : (validationError || "Please enter a valid email address")),

  isValidEmail: (value: string) => matchRegexp(value, REGEX_PATTERNS.EMAIL),

  isValidName: (value: string, validationError?: string) => (
    matchRegexp(value, REGEX_PATTERNS.NAME) ? "" : (validationError || "Please remove any special characters.")),

  minLength: (value: string, length: number, validationError: string) => (
    value.length < length ? (validationError || `Please enter minimum ${length} characters`) : ""
  ),

  isEqual: (value: string, comparingElementValue: string, validationError: string) => (
    value !== comparingElementValue ? validationError : ""),

  phoneNumber: (value: string, validationError: string) => (
    !isPhoneNumber(value) ? validationError : ""
  ),
};

function validationHandler(formValidation: Validation, value: string) {
  const {
    validationType, minLength = 0, validationError = "", comparingElementValue = "",
  } = formValidation;

  switch (validationType) {
  case "IS_EMAIL":
    return validations.isEmail(value, validationError);
  case "IS_NAME":
    return validations.isValidName(value, validationError);
  case "MIN_LENGTH":
    return validations.minLength(value, minLength, validationError);
  case "EQUAL_FIELDS":
    return validations.isEqual(value, comparingElementValue, validationError);
  case "PHONE_NUMBER":
    return validations.phoneNumber(value, validationError);
  default:
    return "";
  }
}

export function isInputValid(value: string, required: boolean, formValidation: Validation) {
  const isFieldMissing = required && !value;

  if (isFieldMissing) {
    return ({
      type: "required",
      message: "Please enter the required field",
    });
  }
  const { validationType } = formValidation;
  const validationResult = validationHandler(formValidation, value);
  return ({
    type: validationType,
    message: validationResult,
  });
}

export function isFormEnabled(formRef, requiredFields: string[], formValidations: Validation[]) {
  if (!formRef || !formRef.current) {
    return false;
  }

  const { elements } = formRef.current;
  const isFieldMissing = !requiredFields.every((field) => elements[field].value);
  if (isFieldMissing) {
    return false;
  }

  const isFieldValidated = formValidations.every(({ validationType, elementName, minLength }) => {
    let validationResult = "";
    switch (validationType) {
    case "IS_EMAIL":
      validationResult = validations.isEmail(elements[elementName].value);
      break;
    case "IS_NAME":
      validationResult = validations.isValidName(elements[elementName].value);
      break;
    case "MIN_LENGTH":
      validationResult = validations.minLength(elements[elementName].value, minLength);
      break;
    default:
      validationResult = "";
    }
    return validationResult === "";
  });
  return isFieldValidated;
}
